import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { supabase } from "@/lib/supabaseClient";
import { getTenantId } from "@/lib/getTenant";

/* ─── Types ─────────────────────────────────────────────────── */
type Priority = "critical" | "warning" | "info";

type AttentionItem = {
  id: string;
  priority: Priority;
  icon: string;
  label: string;
  count: number;
  hint: string;
  href: string;
  visible: boolean;
};

/* ─── Priority styles ────────────────────────────────────────── */
const P_STYLES: Record<Priority, {
  row: string;
  iconBg: string;
  iconColor: string;
  countBg: string;
  btn: string;
  dot: string;
}> = {
  critical: {
    row: "border-red-200 hover:border-red-300 bg-red-50/60",
    iconBg: "bg-red-100",
    iconColor: "text-red-500",
    countBg: "bg-red-500 text-white",
    btn: "text-red-500 hover:bg-red-100",
    dot: "bg-red-400",
  },
  warning: {
    row: "border-amber-200 hover:border-amber-300 bg-amber-50/60",
    iconBg: "bg-amber-100",
    iconColor: "text-amber-600",
    countBg: "bg-amber-500 text-white",
    btn: "text-amber-600 hover:bg-amber-100",
    dot: "bg-amber-400",
  },
  info: {
    row: "border-slate-200 hover:border-[#C8A96E]/30 bg-slate-50/60",
    iconBg: "bg-[#C8A96E]/10",
    iconColor: "text-[#a6853e]",
    countBg: "bg-[#C8A96E] text-white",
    btn: "text-[#a6853e] hover:bg-[#C8A96E]/10",
    dot: "bg-[#C8A96E]",
  },
};

/* ─── Component ──────────────────────────────────────────────── */
export default function DashboardAttention() {
  const [items, setItems]   = useState<AttentionItem[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    const tenantId = await getTenantId();
    if (!tenantId) { setLoading(false); return; }

    const now = new Date();
    const twoDaysAgo = new Date(now.getTime() - 2 * 24 * 60 * 60 * 1000).toISOString();

    const [
      unsignedRes,
      unpaidRes,
      staleLeadsRes,
      waitingClientRes,
      selectionsRes,
      deliveriesRes,
      unreadMsgsRes,
      noAddressRes,
    ] = await Promise.all([

      // 1. Contracts unsigned (active/sent but signature_status != signed)
      supabase
        .from("contracts")
        .select("id", { count: "exact", head: true })
        .eq("tenant_id", tenantId)
        .in("status", ["active", "pending", "sent"])
        .neq("signature_status", "signed"),

      // 2. Unpaid balances — active contracts with balance_amount > 0
      supabase
        .from("contracts")
        .select("id", { count: "exact", head: true })
        .eq("tenant_id", tenantId)
        .in("status", ["active", "signed", "in_progress"])
        .gt("balance_amount", 0),

      // 3. Stale new leads > 2 days
      supabase
        .from("tenant_public_leads")
        .select("id", { count: "exact", head: true })
        .eq("tenant_id", tenantId)
        .eq("status", "new")
        .lt("created_at", twoDaysAgo),

      // 4. Jobs waiting_client
      supabase
        .from("jobs")
        .select("id", { count: "exact", head: true })
        .eq("tenant_id", tenantId)
        .eq("workflow_status", "waiting_client")
        .neq("status", "cancelled"),

      // 5. Jobs with at least one selected photo not yet reviewed (via workflow flag)
      //    Use workflow_status = 'waiting_client' AND status != cancelled as proxy
      //    Real photo check done separately below after Promise.all
      supabase
        .from("jobs")
        .select("id")
        .eq("tenant_id", tenantId)
        .neq("status", "cancelled")
        .limit(1), // placeholder resolved below

      // 6. Deliveries prepared (is_active=false means not yet sent/activated)
      supabase
        .from("job_deliveries")
        .select("id", { count: "exact", head: true })
        .eq("tenant_id", tenantId)
        .eq("is_active", false),

      // 7. Unread client messages (sender_type = client, is_read = false)
      supabase
        .from("client_messages")
        .select("id", { count: "exact", head: true })
        .eq("tenant_id", tenantId)
        .eq("sender_type", "client")
        .eq("is_read", false),

      // 8. Upcoming events without location/address
      supabase
        .from("contracts")
        .select("id", { count: "exact", head: true })
        .eq("tenant_id", tenantId)
        .in("status", ["active", "signed", "in_progress"])
        .or("event_location.is.null,event_location.eq."),
    ]);

    // Photo selections: count distinct jobs that have at least one
    // is_selected=true + selection_reviewed=false photo, filtered by tenant via jobs
    // Strategy: fetch job IDs for this tenant, then count matching photos
    const tenantJobIdsRes = await supabase
      .from("jobs")
      .select("id")
      .eq("tenant_id", tenantId)
      .neq("status", "cancelled");

    const tenantJobIds = (tenantJobIdsRes.data ?? []).map((j: { id: string }) => j.id);

    let photoSelCount = 0;
    if (tenantJobIds.length > 0) {
      // Get distinct job_ids with unreviewed selections
      const { data: photoData } = await supabase
        .from("job_photos")
        .select("job_id")
        .eq("is_selected", true)
        .eq("selection_reviewed", false)
        .in("job_id", tenantJobIds);

      // Count unique job IDs
      const uniqueJobs = new Set((photoData ?? []).map((p: { job_id: string }) => p.job_id));
      photoSelCount = uniqueJobs.size;
    }

    const built: AttentionItem[] = [
      {
        id: "unsigned_contracts",
        priority: "critical",
        icon: "ri-file-close-line",
        label: "Contracts unsigned",
        count: unsignedRes.count ?? 0,
        hint: "Clients haven't signed their contract yet",
        href: "/admin/contracts",
        visible: (unsignedRes.count ?? 0) > 0,
      },
      {
        id: "unpaid_balances",
        priority: "critical",
        icon: "ri-money-dollar-circle-line",
        label: "Unpaid balances",
        count: unpaidRes.count ?? 0,
        hint: "Active contracts with outstanding balance",
        href: "/admin/payments",
        visible: (unpaidRes.count ?? 0) > 0,
      },
      {
        id: "unread_messages",
        priority: "warning",
        icon: "ri-message-3-line",
        label: "Unread client messages",
        count: unreadMsgsRes.count ?? 0,
        hint: "Clients sent messages you haven't read yet",
        href: "/admin/clients",
        visible: (unreadMsgsRes.count ?? 0) > 0,
      },
      {
        id: "stale_leads",
        priority: "warning",
        icon: "ri-user-received-line",
        label: "Leads new +2 days",
        count: staleLeadsRes.count ?? 0,
        hint: "New leads waiting more than 2 days without contact",
        href: "/admin/leads",
        visible: (staleLeadsRes.count ?? 0) > 0,
      },
      {
        id: "waiting_client",
        priority: "warning",
        icon: "ri-time-line",
        label: "Jobs waiting client",
        count: waitingClientRes.count ?? 0,
        hint: "Jobs stalled — waiting on client action",
        href: "/admin/jobs?workflow=waiting_client",
        visible: (waitingClientRes.count ?? 0) > 0,
      },
      {
        id: "photo_selections",
        priority: "info",
        icon: "ri-image-2-line",
        label: "Photo selections submitted",
        count: photoSelCount,
        hint: "Clients submitted photo selections pending your review",
        href: "/admin/jobs",
        visible: photoSelCount > 0,
      },
      {
        id: "deliveries_pending",
        priority: "info",
        icon: "ri-send-plane-line",
        label: "Deliveries not activated",
        count: deliveriesRes.count ?? 0,
        hint: "Prepared deliveries not yet sent to clients",
        href: "/admin/jobs",
        visible: (deliveriesRes.count ?? 0) > 0,
      },
      {
        id: "no_address",
        priority: "info",
        icon: "ri-map-pin-line",
        label: "Events without address",
        count: noAddressRes.count ?? 0,
        hint: "Active contracts missing event location",
        href: "/admin/contracts",
        visible: (noAddressRes.count ?? 0) > 0,
      },
    ];

    setItems(built);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const visible = items.filter((i) => i.visible);
  const criticalCount = visible.filter((i) => i.priority === "critical").length;
  const warningCount  = visible.filter((i) => i.priority === "warning").length;
  const totalCount    = visible.reduce((s, i) => s + i.count, 0);

  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
      {/* Header */}
      <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2.5">
          <span className="w-7 h-7 flex items-center justify-center rounded-lg bg-red-100">
            <i className="ri-alarm-warning-line text-sm text-red-500"></i>
          </span>
          <div>
            <h2 className="text-[#0f172a] font-semibold text-sm">Needs Attention</h2>
            <p className="text-slate-400 text-xs">Action items requiring your follow-up</p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {criticalCount > 0 && (
            <span className="flex items-center gap-1 px-2.5 py-1 rounded-full bg-red-100 text-red-500 text-[11px] font-bold">
              <span className="w-1.5 h-1.5 rounded-full bg-red-400 inline-block"></span>
              {criticalCount} critical
            </span>
          )}
          {warningCount > 0 && (
            <span className="flex items-center gap-1 px-2.5 py-1 rounded-full bg-amber-100 text-amber-600 text-[11px] font-bold">
              <span className="w-1.5 h-1.5 rounded-full bg-amber-400 inline-block"></span>
              {warningCount} warnings
            </span>
          )}
          {!loading && (
            <button
              onClick={load}
              className="w-7 h-7 flex items-center justify-center rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors cursor-pointer"
              title="Refresh"
            >
              <i className="ri-refresh-line text-sm"></i>
            </button>
          )}
        </div>
      </div>

      <div className="p-5">
        {loading ? (
          <div className="flex items-center justify-center gap-2 py-8 text-slate-400">
            <i className="ri-loader-4-line animate-spin text-lg"></i>
            <span className="text-sm">Checking studio status…</span>
          </div>
        ) : visible.length === 0 ? (
          <div className="flex flex-col items-center gap-2 py-8">
            <span className="w-12 h-12 flex items-center justify-center rounded-xl bg-emerald-100 text-emerald-400">
              <i className="ri-checkbox-circle-line text-2xl"></i>
            </span>
            <p className="text-emerald-600 font-semibold text-sm">All clear — nothing needs attention</p>
            <p className="text-slate-400 text-xs">Your studio is fully up to date.</p>
          </div>
        ) : (
          <div className="space-y-2">
            {/* Critical first, then warning, then info */}
            {(["critical", "warning", "info"] as Priority[]).map((prio) => {
              const group = visible.filter((i) => i.priority === prio);
              if (group.length === 0) return null;
              return (
                <div key={prio} className="space-y-2">
                  {group.map((item) => {
                    const s = P_STYLES[item.priority];
                    return (
                      <div
                        key={item.id}
                        className={`flex items-center gap-4 px-4 py-3.5 rounded-xl border transition-all ${s.row}`}
                      >
                        {/* Icon */}
                        <span className={`w-9 h-9 flex items-center justify-center rounded-xl shrink-0 ${s.iconBg} ${s.iconColor}`}>
                          <i className={`${item.icon} text-base`}></i>
                        </span>

                        {/* Info */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="text-[#0f172a] text-sm font-semibold">{item.label}</p>
                            <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${s.countBg}`}>
                              {item.count}
                            </span>
                          </div>
                          <p className="text-slate-400 text-xs mt-0.5">{item.hint}</p>
                        </div>

                        {/* Action */}
                        <Link
                          to={item.href}
                          className={`shrink-0 flex items-center gap-1.5 text-xs font-semibold px-3.5 py-2 rounded-lg border border-transparent hover:border-current transition-all cursor-pointer whitespace-nowrap ${s.btn}`}
                        >
                          View
                          <i className="ri-arrow-right-line text-xs"></i>
                        </Link>
                      </div>
                    );
                  })}
                </div>
              );
            })}

            {/* Total summary footer */}
            <div className="flex items-center justify-between pt-2 mt-1 border-t border-slate-100">
              <p className="text-slate-400 text-xs">
                <span className="font-semibold text-[#0f172a]">{totalCount}</span> total items across{" "}
                <span className="font-semibold text-[#0f172a]">{visible.length}</span> categories
              </p>
              <button
                onClick={load}
                className="flex items-center gap-1 text-xs text-slate-400 hover:text-slate-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-refresh-line text-xs"></i>
                Refresh
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}